///////////////////////////////////////////////////////////////////////////////////////////////////
/// @file   client.ts
/// @brief  Standalone API client for the AWS License Admin Dashboard.
///
///         Handles admin-only endpoints, JWT access_token management, and
///         communicates directly with the AWS backend API (or local test server).
///
/// @copyright Wheatstone Corporation 2025, All rights reserved.
///////////////////////////////////////////////////////////////////////////////////////////////////

import type {
  Entitlement,
  CreateEntitlementRequest,
  UpdateEntitlementRequest,
  ProvisionNanoBundleRequest,
  NanoBundleResult,
  Product,
  EntitlementAgent,
  EntitlementLease,
  EntitlementAlarm,
  AuditLogEntry,
  AuditLogFilters,
  CheckoutRequest,
  DashboardSummary,
} from '../store/entitlement-slice';
import type {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  ImportedKey,
} from '../store/customer-slice';
import type { Server } from '../store/server-slice';
import type { AgentMap } from '../components/fleetmap/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const DEFAULT_TIMEOUT_MS = 30000;

interface ApiResponse<T> {
  data: T;
  success: boolean;
}

interface ApiError {
  error?: string;
  message?: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    createdAt: string;
  };
  token: string;
  expiresAt: string;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private timeoutMs: number;

  constructor(baseUrl: string = API_BASE_URL, timeoutMs: number = DEFAULT_TIMEOUT_MS) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('auth_token');
    this.timeoutMs = timeoutMs;
  }

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { skipAuthRedirect?: boolean; timeoutMs?: number } = {}
  ): Promise<ApiResponse<T>> {
    const { skipAuthRedirect, timeoutMs = this.timeoutMs, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      ...(fetchOptions.body ? { 'Content-Type': 'application/json' } : {}),
      ...fetchOptions.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 401 && !skipAuthRedirect) {
        this.setToken(null);
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = errorData as ApiError;
        throw new Error(error.message || `HTTP error ${response.status}`);
      }

      if (response.status === 204) {
        return { data: undefined as T, success: true } as ApiResponse<T>;
      }

      const data = await response.json();
      return { data: data as T, success: true } as ApiResponse<T>;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeoutMs}ms`);
        }
        throw error;
      }
      throw new Error('Network error');
    }
  }

  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  // ── Authentication ──────────────────────────────────────────────────

  async login(credentials: { username: string; password: string }): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
      skipAuthRedirect: true,
    });

    if (response.data.token) {
      this.setToken(response.data.token);
    }

    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  // ── Dashboard ───────────────────────────────────────────────────────

  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await this.request<DashboardSummary>('/dashboard/summary');
    return response.data;
  }

  // ── Customers ───────────────────────────────────────────────────────

  async getCustomers(): Promise<{ customers: Customer[] }> {
    const response = await this.request<{ customers: Customer[] }>('/admin/customers');
    return response.data;
  }

  async createCustomer(req: CreateCustomerRequest): Promise<Customer> {
    const response = await this.request<Customer>('/admin/customers', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return response.data;
  }

  async updateCustomer(id: number, req: UpdateCustomerRequest): Promise<Customer> {
    const response = await this.request<Customer>(`/admin/customers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(req),
    });
    return response.data;
  }

  async deleteCustomer(id: number): Promise<void> {
    await this.request(`/admin/customers/${id}`, { method: 'DELETE' });
  }

  // ── Imported legacy keys ────────────────────────────────────────────

  async getImportedKeys(customerId: number): Promise<{ customer_id: number; keys: ImportedKey[] }> {
    const response = await this.request<{ customer_id: number; keys: ImportedKey[] }>(
      `/admin/imported-keys${this.buildQueryString({ customer_id: customerId })}`
    );
    return response.data;
  }

  /** Move a license (LICK + its SLICs, addressed by any of its key ids) to another customer. */
  async transferImportedKey(keyId: number, toCustomerId: number): Promise<unknown> {
    const response = await this.request<unknown>(`/admin/imported-keys/${keyId}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ customer_id: toCustomerId }),
    });
    return response.data;
  }

  /** Unclaim a license: remove its seats and key rows so it can be imported again. */
  async releaseImportedKey(keyId: number): Promise<unknown> {
    const response = await this.request<unknown>(`/admin/imported-keys/${keyId}/release`, { method: 'POST' });
    return response.data;
  }

  // ── Entitlements ────────────────────────────────────────────────────

  async getEntitlements(customerId?: number): Promise<{ entitlements: Entitlement[] }> {
    const query = customerId !== undefined ? this.buildQueryString({ customer_id: customerId }) : '';
    const response = await this.request<{ entitlements: Entitlement[] }>(`/admin/entitlements${query}`);
    return response.data;
  }

  async createEntitlement(req: CreateEntitlementRequest): Promise<Entitlement> {
    const response = await this.request<Entitlement>('/admin/entitlements', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return response.data;
  }

  async provisionNanoBundle(req: ProvisionNanoBundleRequest): Promise<NanoBundleResult> {
    const response = await this.request<NanoBundleResult>('/admin/entitlements/provision-nano', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return response.data;
  }

  async updateEntitlement(id: number, req: UpdateEntitlementRequest): Promise<Entitlement> {
    const response = await this.request<Entitlement>(`/admin/entitlements/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(req),
    });
    return response.data;
  }

  async deleteEntitlement(id: number): Promise<void> {
    await this.request(`/admin/entitlements/${id}`, { method: 'DELETE' });
  }

  // ── Products ────────────────────────────────────────────────────────

  async getProducts(): Promise<{ products: Product[] }> {
    const response = await this.request<{ products: Product[] }>('/admin/products');
    return response.data;
  }

  // ── Agents ──────────────────────────────────────────────────────────

  async getEntitlementAgents(): Promise<{ agents: EntitlementAgent[] }> {
    const response = await this.request<{ agents: EntitlementAgent[] }>('/admin/agents');
    return response.data;
  }

  async registerAgent(req: { dev_uuid: string; agent_name: string }): Promise<{ agent_id: string; status: string }> {
    const response = await this.request<{ agent_id: string; status: string }>('/admin/agents', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return response.data;
  }

  // ── License Checkout / Release ──────────────────────────────────────

  async checkoutLicense(req: CheckoutRequest, ip?: string): Promise<unknown> {
    const headers: Record<string, string> = {};
    if (ip) {
      headers['X-Forwarded-For'] = ip;
    }
    const response = await this.request<unknown>('/licenses/checkout', {
      method: 'POST',
      body: JSON.stringify(req),
      headers,
    });
    return response.data;
  }

  async releaseLicenses(leaseIds: number[]): Promise<{ released: number }> {
    const response = await this.request<{ released: number }>('/licenses/release', {
      method: 'POST',
      body: JSON.stringify({ lease_ids: leaseIds }),
    });
    return response.data;
  }

  async releaseAllLicenses(agentId: number): Promise<{ released: number }> {
    // Admin route: works for any customer's agent (the customer route /licenses/release-all only
    // reaches the caller's own leases). Leases are held for the site's grace period, so this is how
    // an admin frees the seats of a site that went away without shutting down.
    const response = await this.request<{ released: number }>(`/admin/agents/${agentId}/release-leases`, {
      method: 'POST',
    });
    return response.data;
  }

  // ── Leases ──────────────────────────────────────────────────────────

  async getAdminLeases(): Promise<{ leases: EntitlementLease[] }> {
    const response = await this.request<{ leases: EntitlementLease[] }>('/admin/leases');
    return response.data;
  }

  // ── Audit Log ─────────────────────────────────────────────────────

  async getAuditLog(filters: AuditLogFilters = {}, limit?: number): Promise<{ logs: AuditLogEntry[] }> {
    const query = this.buildQueryString({
      agent_id: filters.agentId,
      customer_id: filters.customerId,
      action: filters.action,
      limit,
    });
    const response = await this.request<{ logs: AuditLogEntry[] }>(`/admin/audit-log${query}`);
    return response.data;
  }

  // ── Alarms ──────────────────────────────────────────────────────────

  async getDashboardAlarms(): Promise<{ alarms: EntitlementAlarm[] }> {
    const response = await this.request<{ alarms: EntitlementAlarm[] }>('/dashboard/alarms');
    return response.data;
  }

  /** Admin clears an alarm. For LOGIN_LOCKOUT the backend also lifts the login lock. */
  async clearDashboardAlarm(alarmId: number): Promise<{ id: number; cleared: boolean; lock_cleared: boolean }> {
    const response = await this.request<{ id: number; cleared: boolean; lock_cleared: boolean }>(
      `/dashboard/alarms/${alarmId}`,
      { method: 'DELETE' }
    );
    return response.data;
  }

  // ── Servers ────────────────────────────────────────────────────────

  async getAgentMap(hours: number): Promise<AgentMap> {
    const response = await this.request<AgentMap>(`/dashboard/agent-map${this.buildQueryString({ hours })}`);
    return response.data;
  }

  async getServers(customerId?: number): Promise<{ servers: Server[] }> {
    const query = customerId !== undefined ? this.buildQueryString({ customer_id: customerId }) : '';
    const response = await this.request<{ servers: Server[] }>(`/admin/servers${query}`);
    return response.data;
  }

  // ── Cleanup ─────────────────────────────────────────────────────────

  async triggerCleanup(): Promise<void> {
    await this.request('/internal/cleanup', { method: 'POST' });
  }
}

export const apiClient = new ApiClient();

export default apiClient;
